import React, { useState, useEffect } from "react";
import { Alert } from "react-bootstrap";
import { FiArrowLeft } from "react-icons/fi";
import {
  initiateSettlementBatch,
  getCastingDirectorsList,
  getDirectorBankDetails,
  getUnsettledTransactions,
} from "../../services/adminApi";

import CastingDirectorSection from "./components/Settlement/CastingDirectorSection";
import BankDetailsSection from "./components/Settlement/BankDetailsSection";
import TransactionsTableSection from "./components/Settlement/TransactionsTableSection";
import SettlementSummarySection from "./components/Settlement/SettlementSummarySection";
import "./PaymentSettlementCustom.css";

const CreateSettlementPage = ({ onBack, onSuccess }) => {
  // STATES
  const [castingDirectors, setCastingDirectors] = useState([]);
  const [castingDirectorId, setCastingDirectorId] = useState("");
  const [bankInfo, setBankInfo] = useState(null);
  const [unsettledTxs, setUnsettledTxs] = useState([]);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTxs, setLoadingTxs] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // 1. LOAD CASTING DIRECTORS
  useEffect(() => {
    getCastingDirectorsList()
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          const directors = res.data.data;
          setCastingDirectors(directors);
          if (directors.length > 0) setCastingDirectorId(String(directors[0].id));
        }
      })
      .catch((err) => {
        console.error("Error loading Casting Directors:", err);
        setErrorMsg("Failed to load Casting Directors.");
      });
  }, []);

  // 2. LOAD BANK DETAILS + UNSETTLED TRANSACTIONS
  useEffect(() => {
    if (!castingDirectorId) return;

    setBankInfo(null);
    setUnsettledTxs([]);
    setSelectedLogs([]);
    setLoadingTxs(true);
    setSuccessMsg("");
    setErrorMsg("");

    getDirectorBankDetails(castingDirectorId)
      .then((res) => setBankInfo(res.data?.success ? res.data.data : null))
      .catch((err) => {
        console.error("Error fetching bank info:", err);
        setBankInfo(null);
      });

    getUnsettledTransactions(castingDirectorId)
      .then((res) => {
        if (res.data?.success) {
          const txs = Array.isArray(res.data.data) ? res.data.data : [];
          setUnsettledTxs(txs);
          setSelectedLogs(txs.map((t) => t.id || t.payment_log_id));
        } else {
          setUnsettledTxs([]);
          setSelectedLogs([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching unsettled transactions:", err);
        setUnsettledTxs([]);
        setSelectedLogs([]);
      })
      .finally(() => setLoadingTxs(false));
  }, [castingDirectorId]);

  // TOGGLE SINGLE TRANSACTION
  const handleToggle = (id) => {
    setSelectedLogs((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // SELECT / DESELECT ALL
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLogs(unsettledTxs.map((t) => t.id || t.payment_log_id));
    } else {
      setSelectedLogs([]);
    }
  };

  // CALCULATIONS
  const selectedItems = unsettledTxs.filter((item) =>
    selectedLogs.includes(item.id || item.payment_log_id)
  );
  const totalGrossAmount = selectedItems.reduce(
    (acc, curr) => acc + Number(curr.gross_amount ?? curr.amount ?? 0),
    0
  );

  const commType = bankInfo?.commission_type || "Fixed";
  const commVal = Number(bankInfo?.commission_value ?? 0);
  const isTds = Boolean(bankInfo?.is_tds_applicable);
  const tdsVal = Number(bankInfo?.tds_value ?? 0);

  let totalCommissionAmount = 0;
  selectedItems.forEach((tx) => {
    const gross = Number(tx.gross_amount ?? tx.amount ?? 0);
    const commission = commType === "Percentage" ? (gross * commVal) / 100 : commVal;
    totalCommissionAmount += commission;
  });
  totalCommissionAmount = Number(totalCommissionAmount.toFixed(2));

  let totalTdsAmount = 0;
  if (isTds && tdsVal > 0) {
    totalTdsAmount = Number(((totalCommissionAmount * tdsVal) / 100).toFixed(2));
  }

  const finalCDAmount = Number(Math.max(0, totalCommissionAmount - totalTdsAmount).toFixed(2));
  const todayDateStr = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  // SUBMIT HANDLER
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    if (!castingDirectorId) return setErrorMsg("Please select a Casting Director.");
    if (selectedLogs.length === 0)
      return setErrorMsg("Please select at least one transaction to settle.");
    if (!bankInfo)
      return setErrorMsg("Bank account details are not configured for this Casting Director.");

    try {
      setLoading(true);
      const payload = {
        castingDirectorId,
        paymentLogIds: selectedLogs,
        notes: remarks,
        grossAmount: Number(totalGrossAmount.toFixed(2)),
        commissionAmount: totalCommissionAmount,
        tdsAmount: totalTdsAmount,
        netPayableAmount: finalCDAmount,
      };

      const res = await initiateSettlementBatch(payload);
      if (res.data?.success) {
        setSuccessMsg(res.data.message || "Settlement initiated successfully!");
        if (onSuccess) onSuccess();
      } else {
        setErrorMsg(res.data?.message || "Failed to initiate settlement.");
      }
    } catch (err) {
      console.error("Settlement Error:", err);
      setErrorMsg(err.response?.data?.message || "Failed to initiate settlement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bma-settlement-page">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="bma-breadcrumb">
            Home &nbsp;&gt;&nbsp; Payment Settlements &nbsp;&gt;&nbsp; <strong>Create Settlement</strong>
          </div>
          <h1 className="bma-page-title">Create Settlement</h1>
          <p className="bma-page-subtitle">Select casting director and transactions to initiate settlement.</p>
        </div>
        <button type="button" className="bma-btn-outline" onClick={onBack}>
          <FiArrowLeft /> Back to Settlement Master 1
        </button>
      </div>

      {/* ALERT MESSAGES */}
      {successMsg && <Alert variant="success">{successMsg}</Alert>}
      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      <form onSubmit={handleSubmit}>
        <div className="d-flex flex-column gap-4">
          <CastingDirectorSection
            castingDirectors={castingDirectors}
            castingDirectorId={castingDirectorId}
            setCastingDirectorId={setCastingDirectorId}
            bankInfo={bankInfo}
            todayDateStr={todayDateStr}
          />

          <BankDetailsSection bankInfo={bankInfo} />

          <TransactionsTableSection
            loadingTxs={loadingTxs}
            unsettledTxs={unsettledTxs}
            selectedLogs={selectedLogs}
            commType={commType}
            commVal={commVal}
            onToggle={handleToggle}
            onSelectAll={handleSelectAll}
            selectedItems={selectedItems}
            totalGrossAmount={totalGrossAmount}
            totalCommissionAmount={totalCommissionAmount}
          />

          <SettlementSummarySection
            selectedCount={selectedLogs.length}
            totalGrossAmount={totalGrossAmount}
            totalCommissionAmount={totalCommissionAmount}
            isTds={isTds}
            tdsVal={tdsVal}
            totalTdsAmount={totalTdsAmount}
            finalCDAmount={finalCDAmount}
            remarks={remarks}
            setRemarks={setRemarks}
            loading={loading}
            hasBankInfo={Boolean(bankInfo)}
            onBack={onBack}
          />
        </div>
      </form>
    </div>
  );
};

export default CreateSettlementPage;